export function relativeTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** True for machine timestamps like `2026-07-29T09:44:01.000923+00:00`. */
function looksLikeIsoTimestamp(value) {
  return /^\d{4}-\d{2}-\d{2}/.test(String(value).trim());
}

/**
 * Format API timestamps for UI. Leaves human labels (e.g. "2 hrs ago") alone.
 * @param {string|Date|null|undefined} value
 * @param {string} [fallback="—"]
 */
export function formatDateTime(value, fallback = "—") {
  if (value == null || value === "" || value === "—") return fallback;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return fallback;
    return value.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }
  const str = String(value).trim();
  if (!looksLikeIsoTimestamp(str)) return str;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Wall-clock time to show in the thread for a message bubble. */
export function messageDisplayTimestamp(msg) {
  if (!msg) return null;
  const isOutboundSent = msg.direction === "outbound"
    && (msg.status === "sent" || msg.status === "delivered");
  if (isOutboundSent) {
    if (msg.sent_at) return msg.sent_at;
    const completed = msg.metadata?.outbound_dispatch?.completed_at;
    if (completed) return completed;
  }
  return msg.created_at || null;
}

export function formatTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Normalize a website value to a safe http(s) href, or null when invalid. */
export function normalizeWebsiteHref(raw) {
  const value = String(raw || "").trim();
  if (!value || value === "—") return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value) && !/^https?:\/\//i.test(value)) {
    return null;
  }

  let href = value;
  if (!/^https?:\/\//i.test(href)) {
    href = `https://${href.replace(/^\/\//, "")}`;
  }

  try {
    const url = new URL(href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.href;
  } catch {
    return null;
  }
}

export function compactNumber(n) {
  if (n == null) return "—";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function currency(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

export function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
}

export const channelColors = {
  whatsapp: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" },
  email: { bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200", dot: "bg-indigo-500" },
  sms: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", dot: "bg-violet-500" },
  webchat: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200", dot: "bg-sky-500" },
  voice: { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200", dot: "bg-rose-500" },
  telegram: { bg: "bg-cyan-50", text: "text-cyan-700", ring: "ring-cyan-200", dot: "bg-cyan-500" },
  linkedin: { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200", dot: "bg-blue-500" },
};
